import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';

interface DiscoveryQuizContextType {
  isOpen: boolean;
  source: string;
  openQuiz: (source: string) => void;
  closeQuiz: () => void;
  hasCompletedQuiz: boolean;
}

const COMPLETED_KEY = 'discovery_quiz_completed';

const DiscoveryQuizContext = createContext<DiscoveryQuizContextType | undefined>(undefined);

export const DiscoveryQuizProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [source, setSource] = useState('');
  const { userAccount } = useAuth();

  const hasCompletedQuiz = useMemo(() => {
    if (userAccount?.onboardingSurvey?.completed) return true;
    try {
      return localStorage.getItem(COMPLETED_KEY) === 'true';
    } catch {
      return false;
    }
  }, [userAccount]);

  const openQuiz = useCallback((src: string) => {
    setSource(src);
    setIsOpen(true);
  }, []);

  const closeQuiz = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <DiscoveryQuizContext.Provider value={{ isOpen, source, openQuiz, closeQuiz, hasCompletedQuiz }}>
      {children}
    </DiscoveryQuizContext.Provider>
  );
};

export const useDiscoveryQuiz = () => {
  const context = useContext(DiscoveryQuizContext);
  if (context === undefined) {
    throw new Error('useDiscoveryQuiz must be used within a DiscoveryQuizProvider');
  }
  return context;
};
